import React, { useEffect } from 'react'

import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import axios from 'axios'
import { LOADING } from '@app/constants/redux-state'
import locale from 'antd/es/date-picker/locale/ru_RU'
import { IOrganizationLocality } from '@app/interfaces/organization/organization-locality'
import useLocalization from '@app/hooks/useLocalization'
import { getOrganizationLocalities } from '@app/store/actions/organization-locality-actions'
import { useGetOrganizationSchedulesQuery } from '@app/store/rtk/api/organization/organization-schedule-api'
import { IReferenceBillingTariffGroup } from '@app/interfaces/reference-billing'
import NumberInput from '../number-input/number-input'
import { useAddOrUpdateTariffGroupDataMutation } from '@app/store/rtk/api/billing-tariffs-api'
import { openNotificationWithIcon } from '@app/shared/notification/notification'

type Props = {
  visible: boolean
  onCancel: () => void

  editMode: boolean
  selectedTariffGroup?: IReferenceBillingTariffGroup
}

export const AddBillingTariffModal = (props: Props) => {
  const { t } = useTranslation()
  const { localizeData } = useLocalization()
  const [form] = Form.useForm()
  const { data: schedulesData } = useGetOrganizationSchedulesQuery('')

  const editMode = !!props.editMode
  const selectedTariffGroup = props.selectedTariffGroup

  const organizationLocalities = useSelector(
    (v: any) =>
      v.organizationLocality.organizationLocalities as IOrganizationLocality[],
  )

  const state = useSelector((v: any) => v.institutionSchedules.state as string)
  const dispatch = useDispatch()

  const onSuccess = () => {
    // dispatch(
    //   optimisticUpdateReferenceBillingTariffsState('add', {
    //     fullName: form.getFieldValue('fullName'),
    //     username: form.getFieldValue('username'),
    //     id: newUserId,
    //   }),
    // )
  }

  const [updateOrAddTariffGroupData, { isLoading }] =
    useAddOrUpdateTariffGroupDataMutation()
  const handleSubmit = async (values) => {
    try {
      const response = await updateOrAddTariffGroupData({
        localityId: values.location,
        orderNo: values.order_number,
        orderDate: values.order_date.format('YYYY-MM-DD'),
        schedulePrices: values.workTimes.map((item) => ({
          scheduleId: item.id,
          value: +item.one_child * 100,
          discountedValue: +item.two_or_more_child * 100,
        })),
      }).unwrap()

      onSuccess()
      form.resetFields()
      props.onCancel()
    } catch (error) {
      openNotificationWithIcon(
        'error',
        editMode
          ? t('billing.fail_update_tariff')
          : t('billing.fail_create_tariff'),
      )
    }
  }

  useEffect(() => {
    const source = axios.CancelToken.source()
    if (isEmpty(organizationLocalities))
      dispatch(
        getOrganizationLocalities({ source }, t('organizationLocality.error')),
      )
    return function cleanup() {
      source.cancel('Component got unmounted')
    }
  }, [])

  useEffect(() => {
    if (
      schedulesData?.response &&
      schedulesData?.response?.length > 0 &&
      !editMode &&
      props.visible
    ) {
      form.setFieldsValue({
        workTimes: schedulesData?.response
          ?.map((item) => ({
            id: item.id,
            time: localizeData(item.data)?.name ?? item.data?.[0]?.name ?? '',
            one_child: '',
            two_or_more_child: '',
          }))
          .sort((a, b) => a.time.localeCompare(b.time)),
      })
    }
  }, [schedulesData, editMode, props.visible])

  useEffect(() => {
    if (selectedTariffGroup && props.visible && editMode) {
      form.setFieldsValue({
        status: selectedTariffGroup.localityData.status,
        location: selectedTariffGroup.localityData.id,
        workTimes: schedulesData?.response
          ?.map((item) => {
            const currentTariffData = selectedTariffGroup.tariffs.find(
              (i) => i.organizationSchedule.id === item.id,
            )
            return {
              id: item.id,
              time: localizeData(item.data)?.name ?? item.data?.[0]?.name ?? '',
              one_child: currentTariffData
                ? currentTariffData?.value / 100
                : '',
              two_or_more_child: currentTariffData
                ? currentTariffData?.discountedValue / 100
                : '',
            }
          })
          .sort((a, b) => a.time.localeCompare(b.time)),
      })
    }
  }, [props.visible, selectedTariffGroup, editMode])

  return (
    <Modal
      visible={props.visible}
      title={t('billing.change_tariff')}
      onCancel={() => {
        props.onCancel()
        form.resetFields()
      }}
      footer={null}
      width={660}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: true,
        }}>
        <Form.Item
          name="status"
          label={t('billing.status')}
          valuePropName="checked">
          <Switch
            checkedChildren={t('general.active')}
            unCheckedChildren={t('general.inActive')}
          />
        </Form.Item>
        <Form.Item
          name="location"
          label={t('billing.organization_location')}
          rules={[{ required: true, message: t('errors.requiredMessage') }]}>
          <Select disabled={editMode}>
            {organizationLocalities.map((item) => {
              return (
                <Select.Option value={item.id} key={item.id}>
                  {localizeData(item.data)?.name}
                </Select.Option>
              )
            })}
          </Select>
        </Form.Item>

        <Row gutter={10} style={{ marginBottom: 10 }}>
          <Col span={6}>{t('billing.work_time')}</Col>
          <Col span={9}>{t('billing.one_child_sum')}</Col>
          <Col span={9}>{t('billing.two_children_sum')}</Col>
        </Row>
        {state === LOADING ? (
          'Loading...'
        ) : (
          <Form.List
            name={'workTimes'}
            rules={[
              {
                validator(rule, value, callback) {
                  if (value.length === 0) {
                    callback(t('errors.requiredMessage'))
                  } else {
                    callback()
                  }
                },
              },
            ]}>
            {(fields, { add, remove }) => (
              <Row gutter={10}>
                {fields.map((field, index) => (
                  <React.Fragment key={field.key}>
                    <Col span={6}>
                      <Form.Item
                        name={[index, 'time']}
                        rules={[{ required: true }]}
                        style={{
                          pointerEvents: 'none',
                        }}>
                        <Input tabIndex={-1} placeholder="" readOnly />
                      </Form.Item>
                    </Col>
                    <Col span={9}>
                      <Form.Item
                        name={[index, 'one_child']}
                        rules={[{ required: true }]}>
                        <NumberInput />
                        {/* <Input placeholder="" type="number" /> */}
                      </Form.Item>
                    </Col>
                    <Col span={9}>
                      <Form.Item
                        name={[index, 'two_or_more_child']}
                        rules={[{ required: true }]}>
                        <NumberInput />
                        {/* <Input placeholder="" type="number" /> */}
                      </Form.Item>
                    </Col>
                  </React.Fragment>
                ))}
              </Row>
            )}
          </Form.List>
        )}

        <Row gutter={20} justify={'center'}>
          <Col span={24}>
            <p style={{ textAlign: 'center' }}>
              <strong>{t('billing.order_number_and_date')}</strong>
            </p>
          </Col>
          <Col span={9}>
            <Form.Item name={'order_number'} rules={[{ required: true }]}>
              <Input placeholder={t('billing.order_number')} />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name={'order_date'} rules={[{ required: true }]}>
              <DatePicker
                format={'DD-MM-YYYY'}
                style={{ width: '100%' }}
                locale={locale}
                placeholder={t('billing.order_date')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item className="text-right">
          <Button onClick={props.onCancel} style={{ marginRight: 8 }}>
            {t('general.cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {t('general.save')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}
